import { CleaningRequestClientService } from '@services/api/cleaning-request/cleaning-request-client.service';
import { ICleaningRequestModel } from '@services/api/cleaning-request/cleaning-request-model.interface';
import { IApiQueryOptions } from '@services/api/models/api-query-params.model';
import { StatisticsClientService } from '@services/api/statistics/statistics-client.service';
import { addDays, format, subDays } from 'date-fns';
import { map, Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';

export interface DailyChartData {
  date: string; // YYYY-MM-DD format
  ratio: number; // 0-1 value
  percentage: number; // 0-100 value
  totalBookings: number;
  matchingBookings: number;
}

export interface DailyCountData {
  date: string; // YYYY-MM-DD format
  count: number; // Raw count value
}

@Injectable({
  providedIn: 'root'
})
export class DashboardChartDataService {
  private readonly cleaningRequestClientService = inject(CleaningRequestClientService);
  private readonly statisticsClientService = inject(StatisticsClientService);

  private getLast30DaysDateRange(): { startDate: string; endDate: string } {
    const today = new Date();
    const startDate = format(subDays(today, 29), 'yyyy-MM-dd');
    // end_date is exclusive: +1 day so today is included
    const endDate = format(addDays(today, 1), 'yyyy-MM-dd');
    return { startDate, endDate };
  }

  // Backend returns ISO-with-timezone strings ("2026-03-29T00:00:00+01:00"); chart components
  // expect plain "YYYY-MM-DD" for label rendering, so we strip the time portion.
  private normalizeDate(isoDate: string): string {
    return isoDate.substring(0, 10);
  }

  private buildRatioData(date: string, total: number, matching: number): DailyChartData {
    const ratio = total > 0 ? matching / total : 0;
    return {
      date: this.normalizeDate(date),
      ratio,
      percentage: Math.round(ratio * 100),
      totalBookings: total,
      matchingBookings: matching
    };
  }

  getCheckinRatioData(): Observable<DailyChartData[]> {
    const { startDate, endDate } = this.getLast30DaysDateRange();
    return this.statisticsClientService.getCheckinRatio(startDate, endDate).pipe(
      map((response) => response.daily_data.map((e) => this.buildRatioData(e.date, e.total_bookings ?? 0, e.checked_in_bookings ?? 0)))
    );
  }

  getAppUsageData(): Observable<DailyChartData[]> {
    const { startDate, endDate } = this.getLast30DaysDateRange();
    return this.statisticsClientService.getAppUsage(startDate, endDate).pipe(
      map((response) => response.daily_data.map((e) => this.buildRatioData(e.date, e.total_bookings ?? 0, e.app_usage_bookings ?? 0)))
    );
  }

  getStartingBookingsData(): Observable<DailyCountData[]> {
    const { startDate, endDate } = this.getLast30DaysDateRange();
    return this.statisticsClientService.getStartingBookings(startDate, endDate).pipe(
      map((response) => response.daily_data.map((e) => ({ date: this.normalizeDate(e.date), count: e.starting_bookings ?? 0 })))
    );
  }

  getFlatUtilizationData(): Observable<DailyChartData[]> {
    const { startDate, endDate } = this.getLast30DaysDateRange();
    return this.statisticsClientService.getFlatUtilization(startDate, endDate).pipe(
      map((response) => response.daily_data.map((e) => this.buildRatioData(e.date, e.total_flats ?? 0, e.occupied_flats ?? 0)))
    );
  }

  getParkingUtilizationData(): Observable<DailyChartData[]> {
    const { startDate, endDate } = this.getLast30DaysDateRange();
    return this.statisticsClientService.getParkingUtilization(startDate, endDate).pipe(
      map((response) => response.daily_data.map((e) => this.buildRatioData(e.date, e.total_spaces ?? 0, e.occupied_spaces ?? 0)))
    );
  }

  getCleaningSchedulesData(): Observable<DailyCountData[]> {
    const { startDate, endDate } = this.getLast30DaysDateRange();
    return this.statisticsClientService.getCleaningSchedules(startDate, endDate).pipe(
      map((response) => response.daily_data.map((e) => ({ date: this.normalizeDate(e.date), count: e.scheduled_cleanings })))
    );
  }

  getLast30DaysCleaningRequests(): Observable<ICleaningRequestModel[]> {
    const today = new Date();
    const thirtyDaysAgo = subDays(today, 30);

    const queryOptions: Omit<IApiQueryOptions, 'pagination'> = {
      dateFilters: {
        start_date: {
          date: thirtyDaysAgo,
          operator: 'after'
        }
      }
    };

    return this.cleaningRequestClientService.getAllItems('', queryOptions);
  }

  getThisYearCleaningRequests(): Observable<ICleaningRequestModel[]> {
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);

    const queryOptions: Omit<IApiQueryOptions, 'pagination'> = {
      dateFilters: {
        start_date: {
          date: startOfYear,
          operator: 'after'
        }
      }
    };

    return this.cleaningRequestClientService.getAllItems('', queryOptions);
  }

  transformToCleaningSchedulesData(cleaningRequests: ICleaningRequestModel[]): DailyCountData[] {
    const result: DailyCountData[] = [];
    const today = new Date();

    for (let i = 29; i >= 0; i--) {
      const targetDate = subDays(today, i);
      const dateStr = format(targetDate, 'yyyy-MM-dd');

      const dayCleaningRequests = cleaningRequests.filter((request) => {
        if (!request.start_date) return false;
        return format(new Date(request.start_date), 'yyyy-MM-dd') === dateStr;
      });

      result.push({ date: dateStr, count: dayCleaningRequests.length });
    }

    return result;
  }

  transformToYearlyCleaningRequestsData(cleaningRequests: ICleaningRequestModel[]): DailyCountData[] {
    const result: DailyCountData[] = [];
    const currentYear = new Date().getFullYear();

    for (let month = 0; month < 12; month++) {
      const targetDate = new Date(currentYear, month, 1);
      const dateStr = format(targetDate, 'yyyy-MM');

      const monthCleaningRequests = cleaningRequests.filter((request) => {
        if (!request.start_date) return false;
        const requestDate = new Date(request.start_date);
        return requestDate.getFullYear() === currentYear && requestDate.getMonth() === month;
      });

      result.push({ date: dateStr, count: monthCleaningRequests.length });
    }

    return result;
  }
}
